import { Client } from "./Client";

export class InventoryService {
  constructor(private client: Client) {}

  async fetchInventories(params?: {
    type?: string;
    search?: string;
  }): Promise<any> {
    return await this.client.fetchInventories(params);
  }

  async createInventory(inventory: any): Promise<any> {
    return await this.client.createInventory(inventory);
  }

  async getInventory(id: string): Promise<any> {
    return await this.client.getInventory(id);
  }

  async updateInventory(id: string, inventory: any): Promise<any> {
    return await this.client.updateInventory(id, inventory);
  }

  async deleteInventory(id: string): Promise<any> {
    return await this.client.deleteInventory(id);
  }

  async fetchSources(inventoryId: string, params?: {
    pollutant?: string;
    source_type?: string;
  }): Promise<any> {
    return await this.client.fetchInventorySources(inventoryId, params);
  }

  async addSource(inventoryId: string, source: any): Promise<any> {
    return await this.client.addInventorySource(inventoryId, source);
  }

  async editSource(inventoryId: string, sourceId: string, source: any): Promise<any> {
    return await this.client.editInventorySource(inventoryId, sourceId, source);
  }

  async removeSource(inventoryId: string, sourceId: string): Promise<any> {
    return await this.client.removeInventorySource(inventoryId, sourceId);
  }

  async importSpreadsheet(inventoryId: string, file: File, format: string = 'SEMAD'): Promise<any> {
    return await this.client.importInventorySpreadsheet(inventoryId, file, format);
  }

  async importPreview(inventoryId: string, files: File[], options?: { format?: string; pollutant?: string; utm_zone?: number }): Promise<any> {
    return await this.client.importInventoryPreview(inventoryId, files, options);
  }

  async importCommit(inventoryId: string, payload: any): Promise<any> {
    return await this.client.importInventoryCommit(inventoryId, payload);
  }

  async exportExcel(inventoryId: string): Promise<void> {
    return await this.client.exportInventoryExcel(inventoryId);
  }

  async exportAermod(inventoryId: string, pollutant?: string, layout?: "rows" | "columns"): Promise<void> {
    return await this.client.exportInventoryAermod(inventoryId, pollutant, layout);
  }

  async fetchImports(inventoryId: string): Promise<any> {
    return await this.client.fetchInventoryImports(inventoryId);
  }

  async sendToAermod(
    inventoryId: string,
    facilityId: string,
    sourceIds?: string[],
    pollutant?: string
  ): Promise<any> {
    return await this.client.sendInventoryToAermod(
      inventoryId, facilityId, sourceIds, pollutant
    );
  }

  async getOrCreateForFacility(facilityId: string): Promise<any> {
    return await this.client.getOrCreateInventoryForFacility(facilityId);
  }

  async importFromFacility(inventoryId: string, facilityId: string): Promise<any> {
    return await this.client.importFromFacility(inventoryId, facilityId);
  }

  async uploadModelingFile(
    inventoryId: string,
    file: File,
    fileType: 'PFL' | 'SFC' | 'SRTM',
    meta?: { description?: string; region_name?: string; date_start?: string; date_end?: string }
  ): Promise<any> {
    return await this.client.uploadModelingFile(inventoryId, file, fileType, meta);
  }

  async fetchModelingFiles(inventoryId: string, fileType?: string): Promise<any> {
    return await this.client.fetchModelingFiles(inventoryId, fileType);
  }

  async downloadModelingFile(inventoryId: string, fileId: string): Promise<string> {
    return await this.client.downloadModelingFile(inventoryId, fileId);
  }

  async editModelingFile(inventoryId: string, fileId: string, data: { file_name?: string; description?: string }): Promise<any> {
    return await this.client.editModelingFile(inventoryId, fileId, data);
  }

  async deleteModelingFile(inventoryId: string, fileId: string): Promise<any> {
    return await this.client.deleteModelingFile(inventoryId, fileId);
  }

  async generateDiagnosticPackage(
    inventoryId: string,
    params: {
      facility_id: string;
      pfl_file_id: string;
      sfc_file_id: string;
      date_start: string;
      date_end: string;
      avertime?: string;
      source_ids?: string[];
      pollutant?: string;
    }
  ): Promise<void> {
    return await this.client.generateDiagnosticPackage(inventoryId, params);
  }

  async getOutputConfig(inventoryId: string): Promise<any> {
    return await this.client.getOutputConfig(inventoryId);
  }

  async saveOutputConfig(inventoryId: string, config: any): Promise<any> {
    return await this.client.saveOutputConfig(inventoryId, config);
  }

  async parseDiagnosticResults(
    inventoryId: string,
    posFile: File,
    zone: number,
    avertime?: number | string,
    isOdour?: boolean,
    sfcFile?: File,
  ): Promise<any> {
    return await this.client.parseDiagnosticResults(
      inventoryId, posFile, zone, avertime, isOdour, sfcFile
    );
  }

  // ── Emission Records ──

  async getEmissionRecords(inventoryId: string, sourceId: string): Promise<any> {
    return await this.client.getEmissionRecords(inventoryId, sourceId);
  }

  async addEmissionRecord(inventoryId: string, sourceId: string, record: any): Promise<any> {
    return await this.client.addEmissionRecord(inventoryId, sourceId, record);
  }

  async deleteEmissionRecord(inventoryId: string, sourceId: string, recordId: string): Promise<any> {
    return await this.client.deleteEmissionRecord(inventoryId, sourceId, recordId);
  }

  // ── Source Typology ──

  async getSourceTypology(inventoryId: string, sourceId: string): Promise<any> {
    return await this.client.getSourceTypology(inventoryId, sourceId);
  }

  async saveSourceTypology(inventoryId: string, sourceId: string, typology: any): Promise<any> {
    return await this.client.saveSourceTypology(inventoryId, sourceId, typology);
  }

  // ── Emission Profiles ──

  async getEmissionProfiles(inventoryId: string, sourceId: string): Promise<any> {
    return await this.client.getEmissionProfiles(inventoryId, sourceId);
  }

  async addEmissionProfile(inventoryId: string, sourceId: string, profile: any): Promise<any> {
    return await this.client.addEmissionProfile(inventoryId, sourceId, profile);
  }

  async deleteEmissionProfile(inventoryId: string, sourceId: string, profileId: string): Promise<any> {
    return await this.client.deleteEmissionProfile(inventoryId, sourceId, profileId);
  }

  // ── Sync Control ──

  async toggleSync(inventoryId: string, sourceId: string, syncToFlow: boolean): Promise<any> {
    return await this.client.toggleInventorySourceSync(inventoryId, sourceId, syncToFlow);
  }

  async getDeleteInfo(inventoryId: string, sourceId: string): Promise<any> {
    return await this.client.getInventorySourceDeleteInfo(inventoryId, sourceId);
  }
}
